import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { GrafanaQueryApi } from './GrafanaQueryApi';
import type { PrometheusQuery, DataFrameResult, TimeRange, GrafanaAlert, LogEntry } from './types';

const DATASOURCE_UID_PROMETHEUS = 'prometheus';
const DATASOURCE_UID_LOKI = 'loki';

export class GrafanaQueryClient implements GrafanaQueryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getProxyBase(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('proxy')}/grafana/api`;
  }

  async queryPrometheus(
    queries: PrometheusQuery[],
    timeRange: TimeRange,
  ): Promise<DataFrameResult> {
    const base = await this.getProxyBase();

    const body = {
      queries: queries.map(q => ({
        datasource: { type: 'prometheus', uid: DATASOURCE_UID_PROMETHEUS },
        expr: q.expr,
        refId: q.refId,
        legendFormat: q.legendFormat ?? '',
        instant: q.instant ?? false,
        format: q.format ?? 'time_series',
        intervalMs: q.intervalMs ?? 15000,
        maxDataPoints: q.maxDataPoints ?? 500,
      })),
      from: timeRange.from,
      to: timeRange.to,
    };

    const resp = await this.fetchApi.fetch(`${base}/api/ds/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(
        `Grafana query failed (HTTP ${resp.status}): ${text.slice(0, 200)}`,
      );
    }

    return (await resp.json()) as DataFrameResult;
  }

  async queryLabelValues(
    label: string,
    matchers?: string,
  ): Promise<string[]> {
    const base = await this.getProxyBase();
    const url = new URL(
      `${base}/api/datasources/proxy/uid/${DATASOURCE_UID_PROMETHEUS}/api/v1/label/${encodeURIComponent(label)}/values`,
    );
    if (matchers) {
      url.searchParams.set('match[]', matchers);
    }

    const resp = await this.fetchApi.fetch(url.toString());

    if (!resp.ok) {
      throw new Error(`Failed to fetch label values for ${label}`);
    }

    const json = (await resp.json()) as { data: string[] };
    return json.data ?? [];
  }

  async queryAlerts(labelSelector: string): Promise<GrafanaAlert[]> {
    const base = await this.getProxyBase();
    const resp = await this.fetchApi.fetch(
      `${base}/api/prometheus/grafana/api/v1/rules`,
    );

    if (!resp.ok) {
      throw new Error(`Failed to fetch alerts (HTTP ${resp.status})`);
    }

    const json = (await resp.json()) as {
      data: {
        groups: Array<{
          rules: Array<{
            name: string;
            state: string;
            lastEvaluation: string;
            labels: Record<string, string>;
            annotations: Record<string, string>;
            activeAt?: string;
            value?: string;
            alerts?: Array<{
              state: string;
              labels: Record<string, string>;
              annotations: Record<string, string>;
              activeAt?: string;
              value?: string;
            }>;
          }>;
        }>;
      };
    };

    // Parse "key=value, key2=value2" selector into entries
    const selectorEntries = labelSelector.split(',').map(s => {
      const [k, ...rest] = s.trim().split('=');
      return [k.trim(), rest.join('=').trim()] as [string, string];
    });

    const alerts: GrafanaAlert[] = [];
    for (const group of json.data?.groups ?? []) {
      for (const rule of group.rules) {
        const matches = selectorEntries.every(
          ([k, v]) => rule.labels[k] === v,
        );
        if (!matches) continue;

        alerts.push({
          name: rule.name,
          state: rule.state as GrafanaAlert['state'],
          lastEvaluation: rule.lastEvaluation,
          labels: rule.labels,
          annotations: rule.annotations ?? {},
          activeAt: rule.alerts?.[0]?.activeAt ?? rule.activeAt,
          value: rule.alerts?.[0]?.value ?? rule.value,
        });
      }
    }

    return alerts;
  }

  async queryLoki(
    logql: string,
    timeRange: TimeRange,
    limit = 200,
  ): Promise<LogEntry[]> {
    const base = await this.getProxyBase();

    const body = {
      queries: [
        {
          datasource: { type: 'loki', uid: DATASOURCE_UID_LOKI },
          expr: logql,
          refId: 'logs',
          maxLines: limit,
          queryType: 'range',
        },
      ],
      from: timeRange.from,
      to: timeRange.to,
    };

    const resp = await this.fetchApi.fetch(`${base}/api/ds/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(
        `Loki query failed (HTTP ${resp.status}): ${text.slice(0, 200)}`,
      );
    }

    const json = (await resp.json()) as DataFrameResult;
    const entries: LogEntry[] = [];

    const frames = json.results?.logs?.frames ?? [];
    for (const frame of frames) {
      const { schema, data } = frame;
      if (!data?.values?.length) continue;

      const fieldNames = schema.fields.map(f => f.name);
      const tsIdx = fieldNames.indexOf('timestamp') !== -1
        ? fieldNames.indexOf('timestamp')
        : fieldNames.findIndex(n => n === 'Time' || n === 'time' || n === 'ts');
      const lineIdx = fieldNames.indexOf('Line') !== -1
        ? fieldNames.indexOf('Line')
        : fieldNames.findIndex(n => n === 'line' || n === 'body');
      const labelsIdx = fieldNames.indexOf('labels') !== -1
        ? fieldNames.indexOf('labels')
        : fieldNames.findIndex(n => n === 'Labels');

      if (tsIdx === -1 || lineIdx === -1) continue;

      const timestamps = data.values[tsIdx] as number[];
      const lines = data.values[lineIdx] as string[];
      const labelValues = labelsIdx !== -1 ? data.values[labelsIdx] as Record<string, string>[] : [];

      for (let i = 0; i < timestamps.length; i++) {
        entries.push({
          timestamp: typeof timestamps[i] === 'number'
            ? timestamps[i]
            : new Date(timestamps[i] as unknown as string).getTime(),
          line: lines[i] ?? '',
          labels: labelValues[i] ?? {},
        });
      }
    }

    return entries.sort((a, b) => b.timestamp - a.timestamp);
  }
}
